import type { Category, CategoryTree } from "../models/category";
import { categoryTree } from "./categoryTree";
import { relevantSubcategories } from "./relevantSubcategories";

const byName = ([, a]: [string, { name: string }], [, b]: [string, { name: string }]) => a.name.localeCompare(b.name);

const assertThreeCharKeys = (source: CategoryTree) => {
  const invalidKeys = Object.keys(source).filter((key) => key.length !== 3);

  for (const category of Object.values(source)) {
    invalidKeys.push(...Object.keys(category.subcategories).filter((key) => key.length !== 3));
  }

  if (invalidKeys.length > 0) {
    throw new Error(`Invalid keys found: ${invalidKeys.join(", ")}. All keys in the category tree must be exactly three characters long.`);
  }
};

const sortTree = (source: CategoryTree): CategoryTree =>
  Object.fromEntries(
    Object.entries(source)
      .sort(byName)
      .map(([key, category]) => [key, { ...category, subcategories: Object.fromEntries(Object.entries(category.subcategories).sort(byName)) }]),
  );

let sortedTree: CategoryTree | undefined;

/** The category tree with categories and subcategories sorted alphabetically by name. */
export const getCategoryTree = (): CategoryTree => {
  if (!sortedTree) {
    assertThreeCharKeys(categoryTree);
    sortedTree = sortTree(categoryTree);
  }
  return sortedTree;
};

export const getCategory = (categoryId: string) => {
  const category = getCategoryTree()[categoryId];
  if (!category) {
    throw new Error(`Invalid category '${categoryId}'. This should have been validated earlier.`);
  }
  return category;
};

export const getSubcategory = (category: Category, subcategoryId: string) => {
  const subcategory = category.subcategories[subcategoryId];
  if (!subcategory) {
    const valid = Object.keys(category.subcategories).join(", ");
    throw new Error(
      `Invalid subcategory '${subcategoryId}' for category '${category.name}'. Must be one of: ${valid}. This should have been validated earlier.`,
    );
  }
  return subcategory;
};

export const getCategoryAndSubcategory = (categoryId: string, subcategoryId: string) => {
  const category = getCategory(categoryId);
  return { category, subcategory: getSubcategory(category, subcategoryId) };
};

export const getRelevantSubcategoryIds = (categoryId: string, subcategoryId: string): string[] | undefined => {
  return relevantSubcategories[`${categoryId}.${subcategoryId}`];
};
